<template>
  <div class="overflow-x-auto">
    <div v-if="errorMessage" class="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
      {{ errorMessage }}
    </div>
    <table class="min-w-full bg-white border border-gray-200 rounded-md shadow">
      <thead>
        <tr class="bg-gray-100">
          <th class="py-3 px-4 text-left text-gray-600 font-semibold">
            Facility Name
          </th>
          <th class="py-3 px-4 text-left text-gray-600 font-semibold">
            Admin Name
          </th>
          <th class="py-3 px-4 text-left text-gray-600 font-semibold">
            Admin Email
          </th>
          <th class="py-3 px-4 text-left text-gray-600 font-semibold">
            Current Bill
          </th>
          <th class="py-3 px-4 text-left text-gray-600 font-semibold">
            Last Payment Date
          </th>
          <th class="py-3 px-4 text-left text-gray-600 font-semibold">
            Last Payment Amount
          </th>
          <th class="py-3 px-4 text-left text-gray-600 font-semibold">
            Status
          </th>
          <th class="px-6 py-3 text-right text-xs text-gray-600 font-semibold">
            Actions
          </th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <tr v-if="isLoading" class="bg-gray-50">
          <td colspan="9" class="px-6 py-3 text-center text-gray-500">
            <svg
              class="animate-spin inline-block h-5 w-5 text-orange-400 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading facilities...
          </td>
        </tr>
        <tr v-else-if="!paginatedFacilities.length" class="bg-gray-50">
          <td colspan="9" class="px-6 py-3 text-center text-gray-500">
            No facilities found.
          </td>
        </tr>
        <tr
          v-else
          v-for="facility in paginatedFacilities"
          :key="facility.FacilityId"
          class="border-t"
        >
          <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {{ facility.FacilityName || 'N/A' }}
          </td>
          <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {{ facility.admin_name || 'N/A' }}
          </td>
          <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {{ facility.admin_email || 'N/A' }}
          </td>
          <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {{ formatCurrency(facility.current_bill || 0) }}
          </td>
          <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {{ facility.last_payment_date || 'N/A' }}
          </td>
          <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {{ formatCurrency(facility.last_payment_amount || 0) }}
          </td>
          <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
            {{ facility.status || 'No Bills' }}
          </td>
          <td class="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
            <button
              @click="openModal(facility)"
              class="px-3 py-1 bg-orange-400 text-white rounded-md hover:bg-orange-500"
            >
              Edit
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="flex justify-center items-center my-8">
      <div class="flex space-x-2">
        <button
          @click="goToPreviousPage"
          :disabled="pagination.current_page === 1"
          class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          :class="{
            'bg-blue-900 text-white': pagination.current_page === page,
            'text-gray-600': pagination.current_page !== page
          }"
          class="px-3 py-1 rounded-md hover:bg-blue-100"
        >
          {{ page }}
        </button>
        <span v-if="showEllipsis" class="px-3 py-1 text-gray-600">...</span>
        <button
          v-if="showLastPage"
          @click="goToPage(pagination.total_pages)"
          :class="{
            'bg-blue-900 text-white': pagination.current_page === pagination.total_pages,
            'text-gray-600': pagination.current_page !== pagination.total_pages
          }"
          class="px-3 py-1 rounded-md hover:bg-blue-100"
        >
          {{ pagination.total_pages }}
        </button>
        <button
          @click="goToNextPage"
          :disabled="pagination.current_page === pagination.total_pages"
          class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { facilitiesAPI } from '@/services';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
  filters: {
    type: Object,
    default: () => ({ start_date: '', end_date: '' }),
  },
});

const emit = defineEmits(['edit-facility']);

const facilities = ref([]);
const isLoading = ref(false);
const errorMessage = ref(null);
const isFetching = ref(false);
const pagination = ref({
  current_page: 1,
  total_pages: 1,
  total_count: 0,
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'KES',
  }).format(value);
};

const fetchFacilities = debounce(async (page = 1) => {
  if (isFetching.value) {
    console.warn('BillingTab: Fetch aborted: already fetching');
    return;
  }

  console.log('BillingTab: fetchFacilities called, searchQuery:', props.searchQuery, 'filters:', props.filters, 'page:', page);
  isFetching.value = true;
  isLoading.value = true;
  errorMessage.value = null;

  try {
    const startTime = Date.now();
    const response = await facilitiesAPI.getAll({
      search: props.searchQuery,
      start_date: props.filters.start_date,
      end_date: props.filters.end_date,
      page: page,
      per_page: 10,
    });
    const duration = Date.now() - startTime;
    console.log('BillingTab: Facilities API response:', JSON.stringify(response.data, null, 2), `Took: ${duration}ms`);
    if (response.data && response.data.success) {
      facilities.value = response.data.data.map(facility => ({
        FacilityId: facility.FacilityId,
        FacilityName: facility.FacilityName,
        admin_name: facility.users?.[0]?.Username || 'N/A',
        admin_email: facility.users?.[0]?.Email || 'N/A',
        current_bill: facility.current_bill || 0.0,
        last_payment_date: facility.last_payment_date || 'N/A',
        last_payment_amount: facility.last_payment_amount || 0.0,
        grace_period: facility.grace_period || 'N/A',
        status: facility.status || 'No Bills',
        users: facility.users || [],
      }));
      pagination.value = response.data.pagination || {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
      };
    } else {
      throw new Error(response.data?.message || 'Failed to fetch facilities');
    }
  } catch (error) {
    console.error('BillingTab: Error fetching facilities:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    errorMessage.value = error.response?.data?.message || 'Failed to fetch facilities. Please try again.';
    facilities.value = [];
  } finally {
    isLoading.value = false;
    isFetching.value = false;
  }
}, 500);

const paginatedFacilities = computed(() => facilities.value);

const visiblePages = computed(() => {
  const pages = [];
  const current = pagination.value.current_page;
  const total = pagination.value.total_pages;
  const range = 2;
  for (let i = Math.max(1, current - range); i <= Math.min(total, current + range); i++) {
    pages.push(i);
  }
  return pages;
});

const showEllipsis = computed(() => pagination.value.total_pages > 5 && pagination.value.current_page < pagination.value.total_pages - 2);

const showLastPage = computed(() => pagination.value.total_pages > 5 && pagination.value.current_page < pagination.value.total_pages - 2);

const goToPage = (page) => {
  if (page >= 1 && page <= pagination.value.total_pages) {
    pagination.value.current_page = page;
    fetchFacilities(page);
  }
};

const goToPreviousPage = () => {
  if (pagination.value.current_page > 1) {
    pagination.value.current_page--;
    fetchFacilities(pagination.value.current_page);
  }
};

const goToNextPage = () => {
  if (pagination.value.current_page < pagination.value.total_pages) {
    pagination.value.current_page++;
    fetchFacilities(pagination.value.current_page);
  }
};

const openModal = async (facility) => {
  try {
    console.log('BillingTab: Fetching facility details for ID:', facility.FacilityId);
    const response = await facilitiesAPI.getOne(facility.FacilityId);
    console.log('BillingTab: Facility details response:', JSON.stringify(response.data, null, 2));
    if (response.data && response.data.success && response.data.data) {
      const facilityData = response.data.data;
      emit('edit-facility', {
        FacilityId: facilityData.FacilityId,
        FacilityName: facilityData.FacilityName,
        Type: facilityData.Type || '',
        Owner: facilityData.Owner || '',
        County: facilityData.County || '',
        SubCounty: facilityData.SubCounty || '',
        Division: facilityData.Division || '',
        Location: facilityData.Location || '',
        SubLocation: facilityData.SubLocation || '',
        Constituency: facilityData.Constituency || '',
        NearestTown: facilityData.NearestTown || '',
        Latitude: facilityData.Latitude || null,
        Longitude: facilityData.Longitude || null,
        Commission: facilityData.Commission || 50.0,
        maximum_bill_amount: facilityData.maximum_bill_amount || 0,
        kra_pin: facilityData.kra_pin || '',
        e_tims_no: facilityData.e_tims_no || '',
        mtiba_username: facilityData.mtiba_username || '',
        mtiba_password: facilityData.mtiba_password || '',
        users: facilityData.users?.map(user => ({
          UserId: user.UserId,
          Username: user.Username,
          Email: user.Email,
          PhoneNumber: user.PhoneNumber || '',
          DateOfBirth: user.DateOfBirth || '',
          Gender: user.Gender || null,
          IsActive: user.IsActive || true,
          LastLoginDate: user.LastLoginDate || new Date().toISOString(),
          CreatedBy: user.CreatedBy || 'system',
          CreatedAt: user.CreatedAt || new Date().toISOString(),
          UserType: user.UserType || 'Admin',
        })) || [],
      });
    } else {
      throw new Error(response.data?.message || 'Failed to fetch facility details');
    }
  } catch (error) {
    console.error('BillingTab: Error fetching facility details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    errorMessage.value = error.response?.data?.message || 'Failed to fetch facility details. Please try again.';
    // Fallback to minimal data
    emit('edit-facility', {
      FacilityId: facility.FacilityId,
      FacilityName: facility.FacilityName,
      users: facility.users,
    });
  }
};

// Expose fetchFacilities for parent component
defineExpose({ fetchFacilities });

// Initialize fetch on mount
onMounted(() => {
  console.log('BillingTab: Mounted, fetching facilities');
  fetchFacilities();
});

// Watch for prop changes
watch(
  () => [props.searchQuery, props.filters.start_date, props.filters.end_date],
  () => {
    console.log('BillingTab: Props changed:', {
      searchQuery: props.searchQuery,
      filters: props.filters,
    });
    pagination.value.current_page = 1;
    fetchFacilities(1);
  },
  { deep: true }
);
</script>

<style scoped></style>